/**
 * AVBP Portal — WhatsApp "Messages" workspace (client transport + pure
 * display helpers).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  The "Messages" screen is a read-all + reply console over the WhatsApp
 *  machinery that already runs in production. The WA pipeline collections
 *  (wa_inbound / wa_status / draft_contacts / wa_agent_threads) are
 *  server-only by firestore.rules (read,write:false) — the portal can NEVER
 *  read them directly. So, exactly like adminActionQueue.ts, this module
 *  talks ONLY through two admin/role-guarded callables:
 *
 *    waListConversations — merged visible list (+ one thread + 24h window
 *                          state + the approved-template list)
 *    waSendManualReply   — send ONE explicit reply (free-form in window,
 *                          approved template outside). No bulk shape exists.
 *
 *  Visibility (admin → all · staff → own-projects · client → rejected) is
 *  enforced SERVER-SIDE in the callable; the client verdict is never
 *  trusted. The pure helpers (windowLabel / fmtClock / sortConversations)
 *  are exported so the Node test runner exercises the display contract
 *  without React or Firebase (same discipline as adminActionQueue.ts).
 * ─────────────────────────────────────────────────────────────────────────
 */

export type ConvKind = 'client' | 'agent' | 'triage';
export type MsgWho = 'client' | 'human' | 'ai';
export type MsgDirection = 'in' | 'out';

export interface ConversationRow {
  key: string;
  kind: ConvKind;
  name: string;
  phone: string | null;
  /** Sender's WhatsApp display name (Send-Picker name-fallback #2); null if uncaptured. */
  waProfileName?: string | null;
  projectId: string | null;
  projectRef: string | null;
  lastMessage: string;
  lastMessageAt: number | null;
  aiHandled: boolean;
  triage: boolean;
  unread: number;
  /** Meta `phone_number_id` of OUR number that received the LAST inbound
   *  on this conversation. Threaded since commit 8319bb1 (multi-sender —
   *  +91 99933 17711 + +91 91793 03585). `null` for legacy rows that
   *  predate that commit — the renderer must skip the badge then. */
  receivingPhoneNumberId?: string | null;
  /** AI agent / internal bot thread? (conv-index `kind:'agent'` →
   *  `agent:<staffId>`). These auto-reply and must be visually separated
   *  from real customer conversations. */
  aiBot?: boolean;
  /** Human has TAKEN OVER this conversation — the inbound AI-auto-reply is
   *  paused server-side (conv-index `aiPaused`). Default false (AI replies).
   *  Surfaced so the composer shows the "you're replying manually" state. */
  aiPaused?: boolean;
  /** The `clients/{cid}` this conversation belongs to (server-resolved on the
   *  wa_conversations index). Carried on EVERY row now so the Person lens can
   *  filter/attribute a thread to a contact without a parallel read. `null`
   *  when the sender is not yet linked to a contact (unknown / triage). */
  contactId?: string | null;
}

export interface MessageAttachment {
  kind: 'image' | 'document' | string;
  fileName: string | null;
  contentType: string | null;
  downloadURL: string | null;
  /** Storage object path of the received bytes (`projects/{pid}/artefacts/…`).
   *  Server-supplied on inbound/outbound media so an attachment can be
   *  FORWARDED without re-uploading — the send callable mints a fresh signed
   *  URL from this path when `downloadURL` is absent/expired. `null` on legacy
   *  rows that predate storage-path capture (forward falls back to the URL). */
  storagePath?: string | null;
  /** Storage bucket the object lives in (defaults to the app's default bucket
   *  server-side when null). Carried so a forward is bucket-explicit. */
  storageBucket?: string | null;
}

export interface WorkspaceMessage {
  id: string | null;
  direction: MsgDirection;
  who: MsgWho;
  body: string;
  at: number | null;
  deliveryState: string | null;
  attachment?: MessageAttachment | null;
  /** Meta `phone_number_id` of OUR number this individual INBOUND
   *  message landed on. Only inbounds carry it (outbounds we send via
   *  Cloud API use a single account-level number — see runbook). */
  receivingPhoneNumberId?: string | null;
  /** AD-028 — the WhatsApp message id (the target wamid for reacting). */
  waMessageId?: string | null;
  /** AD-028 — emoji reactions pinned on this message, keyed by reactor
   *  identity (client phone for inbound; 'self' for a portal-sent reaction). */
  reactions?: Record<string, { emoji: string; at?: number | null; by?: string }> | null;
  /** Raw Meta message type ('text' | 'image' | 'location' | …) when stored. */
  msgType?: string | null;
  /** WhatsApp location pin, threaded by the router from raw Meta `location`. */
  location?: {
    lat?: number | null;
    lng?: number | null;
    name?: string | null;
    address?: string | null;
    mapUrl?: string | null;
  } | null;
}

export interface WindowState {
  open: boolean;
  lastInboundMs: number | null;
  closesAtMs: number | null;
  remainingMs: number;
}

export interface ApprovedTemplate {
  name: string;
  messageType: string | null;
  language: string;
  /** Meta's own category (UTILITY / MARKETING / AUTHENTICATION). */
  category?: string | null;
  /**
   * Meta's own body text. Present for every template the live catalog returns,
   * so the picker can preview templates that were never registered locally.
   */
  body?: string;
  /** Blank count derived from Meta's body. */
  paramCount?: number | null;
}

export interface ThreadView {
  key: string;
  messages: WorkspaceMessage[];
  window: WindowState;
  phone: string | null;
  /** Whether more older messages exist beyond this page. */
  hasMore?: boolean;
  /** Oldest message `at` in this batch — pass as `beforeMs` to load older. */
  nextCursor?: number | null;
}

export interface ListResult {
  role: 'admin' | 'staff';
  conversations: ConversationRow[];
  approvedTemplates: ApprovedTemplate[];
  thread?: ThreadView;
  /** Pagination cursor — pass back as `cursor` to fetch older
   *  conversations. `null` when there are no more (the list now reads the
   *  denormalised wa_conversations index, newest-first, paged). */
  nextCursor?: number | null;
  /**
   * The requested conversation's own row, sent ONLY when that conversation is
   * not on the paginated page. Lets the thread pane render instead of showing
   * its empty state (owner report, 2026-08-02: opening a contact last messaged
   * in June looked like a dead click).
   */
  offPageConversation?: ConversationRow | null;
}

export interface SendResult {
  ok: boolean;
  mode: 'freeform' | 'template' | 'media';
  waMessageId: string | null;
  templateName: string | null;
}

export interface MarkReadResult {
  ok: boolean;
  /** true → the server actually cleared a non-zero unread (vs. no-op). */
  cleared: boolean;
}

/** Metadata of an uploaded attachment, handed to the send callable. */
export interface AttachmentInput {
  kind: 'image' | 'document';
  /** Firebase Storage download URL — Meta fetches the bytes from here. When
   *  forwarding an EXISTING attachment this may be empty; the server then
   *  mints a fresh signed URL from `storagePath`. */
  link: string;
  fileName: string;
  contentType: string;
  storagePath: string;
  /** Bucket the object lives in — only set on a FORWARD of an existing
   *  attachment (a fresh upload lives in the default bucket). Optional so
   *  every existing caller stays unchanged. */
  storageBucket?: string | null;
}

export type ConvFilter = 'all' | 'unread' | 'triage' | 'mine' | 'customers' | 'bots';

/* ════════════════════ pure display helpers ════════════════════ */

/** Human, glance-able 24h-window strip text (mirrors the mockup copy). */
export function windowLabel(w: WindowState): string {
  if (w.open) {
    const h = Math.floor(w.remainingMs / 3_600_000);
    const m = Math.floor((w.remainingMs % 3_600_000) / 60_000);
    return `Session open — reply freely · closes in ~${h}h ${m}m`;
  }
  return 'Outside the 24-hour window — free typing is blocked by WhatsApp · choose an approved template.';
}

/** Short clock for a row/message timestamp; defensive, never throws. */
export function fmtClock(ms: number | null): string {
  if (!ms) return '';
  try {
    const d = new Date(ms);
    const today = new Date();
    const sameDay =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();
    return sameDay
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

/**
 * PURE — is this an AI-bot / internal (agent) thread? Staff WhatsApp
 * messages route to `agent:<staffId>` threads that auto-reply (the "AI ·
 * auto-reply" bubbles). They are distinguished by the conv-index `kind`
 * mapping to the row `kind:'agent'`. `aiHandled` is a corroborating signal
 * the server sets on agent rows, but `kind` is the authoritative key. These
 * must be visually separated from real customer chats.
 */
export function isBotRow(r: ConversationRow): boolean {
  return r.kind === 'agent';
}

/**
 * Apply a list filter. Pure so the test locks the filter contract.
 * `all` → everything · `unread` → unread>0 · `triage` → triage rows ·
 * `mine` → rows that have a project (staff's list is already server-scoped
 * to their projects, so "mine" simply means "has a project", not triage) ·
 * `customers` → real customer chats (NOT the AI-bot/internal agent threads) ·
 * `bots` → the AI-bot / internal agent threads only.
 *
 * `receivingFilter` (item 2) is an INDEPENDENT axis: when set to a specific
 * Meta phone-number-id, keep only rows whose last inbound landed on THAT one
 * of our numbers. Legacy rows (no receiving id) survive only under 'all'.
 */
export function applyFilter(
  rows: ConversationRow[],
  f: ConvFilter,
  search: string,
  receivingFilter: string | 'all' = 'all',
): ConversationRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f === 'unread' && !(r.unread > 0)) return false;
    if (f === 'triage' && !r.triage) return false;
    if (f === 'mine' && !r.projectId) return false;
    if (f === 'customers' && isBotRow(r)) return false;
    if (f === 'bots' && !isBotRow(r)) return false;
    if (receivingFilter !== 'all') {
      // Only rows received on the chosen number. A row with no receiving id
      // (legacy, pre-8319bb1) never matches a specific number → it appears
      // ONLY under the [All] number segment.
      if ((r.receivingPhoneNumberId || '') !== receivingFilter) return false;
    }
    if (q) {
      const hay = `${r.name} ${r.phone || ''} ${r.projectRef || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** Stable sort: newest activity first, then key (server already sorts;
 *  this is a defensive client-side guarantee + test anchor). */
export function sortConversations(
  rows: ConversationRow[],
): ConversationRow[] {
  return [...rows].sort((a, b) => {
    const d = (b.lastMessageAt || 0) - (a.lastMessageAt || 0);
    if (d !== 0) return d;
    return a.key.localeCompare(b.key);
  });
}

/** Count summary for the filter chips. */
export function filterCounts(rows: ConversationRow[]): {
  all: number;
  unread: number;
  triage: number;
  mine: number;
  customers: number;
  bots: number;
} {
  return {
    all: rows.length,
    unread: rows.filter((r) => r.unread > 0).length,
    triage: rows.filter((r) => r.triage).length,
    mine: rows.filter((r) => r.projectId).length,
    customers: rows.filter((r) => !isBotRow(r)).length,
    bots: rows.filter((r) => isBotRow(r)).length,
  };
}

/* ════════════════════ multi-sender (item 2/3) — pure helpers ════════════════════ */

/**
 * Build the options for the inbox "Showing:" number DROPDOWN (replaces the
 * earlier chip-row — a dropdown beats a wrapping chip-row on a narrow phone,
 * and the portal ships inside the Capacitor app). The first option is always
 * "All numbers"; then one option per CONFIGURED role (Sales / Client Care …)
 * from `receivingPhone.ts` `RECEIVING_NUMBER_ROLES`, in config order; then any
 * UNCONFIGURED number that actually has traffic (so a new line is reachable
 * before someone gives it a role). The role LABEL is config-driven (one place
 * to rename); the digit sub-line comes from the SAME badge model the row chips
 * use, so labels never diverge.
 *
 * A row with no receiving id (legacy, pre-8319bb1) is counted only under "All
 * numbers" — exactly as `applyFilter` treats it.
 *
 * @param roles  RECEIVING_NUMBER_ROLES (config-driven role labels)
 * @param badgeOf  formatReceivingPhoneBadge (single source of the digit line)
 * @returns options — `{ id:'all'|<phoneId>, label, line, count }`
 */
export function receivingFilterOptions(
  rows: ConversationRow[],
  roles: Array<{ phoneId: string; label: string; line: string }>,
  badgeOf: (
    id: string | null | undefined,
  ) => { label: string; fullPhone: string } | null,
): Array<{ id: string; label: string; line: string; count: number }> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const id = r.receivingPhoneNumberId || '';
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const options: Array<{
    id: string;
    label: string;
    line: string;
    count: number;
  }> = [{ id: 'all', label: 'All numbers', line: '', count: rows.length }];
  const seen = new Set<string>();
  // Configured roles first, in config order — listed even with 0 traffic so
  // the dropdown is stable and predictable for the owner.
  for (const role of roles) {
    seen.add(role.phoneId);
    options.push({
      id: role.phoneId,
      label: role.label,
      line: role.line,
      count: counts.get(role.phoneId) || 0,
    });
  }
  // Any unconfigured number that has traffic — fall back to its digit label.
  for (const [id, count] of counts) {
    if (seen.has(id)) continue;
    const badge = badgeOf(id);
    options.push({
      id,
      label: badge ? badge.label : id,
      line: badge ? badge.fullPhone : id,
      count,
    });
  }
  return options;
}

/**
 * Which OF OUR numbers a manual reply on THIS conversation will send from —
 * the portal-side mirror of the server dual-number send resolver
 * (avbp_wa_router resolveSendIdentity / lib/waSendIdentity): the reply goes
 * out from the SAME number the conversation's last inbound landed on, and
 * ANY legacy / unknown / null receiving id defaults to the PRIMARY number.
 * So the composer can honestly say "Replying from +91 …" and stay correct
 * for legacy rows (which reply from primary). Returns the full +91 phone.
 *
 * @param receivingPhoneNumberId  the open conversation's receiving id
 * @param badgeOf  formatReceivingPhoneBadge (so labels never diverge)
 * @param primaryPhoneId  the PRIMARY Meta phone-number-id (default fallback)
 */
export function replyFromPhone(
  receivingPhoneNumberId: string | null | undefined,
  badgeOf: (
    id: string | null | undefined,
  ) => { fullPhone: string; classKey: string } | null,
  primaryPhoneId: string,
): string {
  // A KNOWN receiving id (one of OUR two numbers) → reply from that same
  // number. A badge with classKey 'other' is an UNKNOWN id — the server's
  // resolveSendIdentity defaults those to PRIMARY (not the raw id), so we
  // must NOT echo it here, or the composer would claim a wrong sender.
  const direct = badgeOf(receivingPhoneNumberId);
  if (receivingPhoneNumberId && direct && direct.classKey !== 'other') {
    return direct.fullPhone;
  }
  // Legacy / unknown / null → default to PRIMARY (matches the server
  // resolveSendIdentity default-to-primary branch exactly).
  const primary = badgeOf(primaryPhoneId);
  return primary ? primary.fullPhone : '+91 99933 17711';
}

/** PRIMARY Meta phone-number-id (the lead-ack number, +91 99933 17711).
 *  The single place the portal names the default-send number — kept in sync
 *  with the server WA_PHONE_NUMBER_ID + receivingPhone.ts KNOWN map. */
export const PRIMARY_PHONE_NUMBER_ID = '926096937247472';

/* ════════════════════ attachment (ISSUE 2) — pure helpers ════════════════════ */

/**
 * PURE — WhatsApp only carries `image` or `document`. Everything that is
 * not a (supported) image is sent as a `document` (PDF, docx, …). This
 * mirrors the server's decideMediaSend kind-normalisation.
 */
export function attachKindOf(contentType: string): 'image' | 'document' {
  const t = (contentType || '').toLowerCase();
  // WA image types: jpeg/png (webp is unreliable as an image msg → doc).
  if (t === 'image/jpeg' || t === 'image/jpg' || t === 'image/png') {
    return 'image';
  }
  return 'document';
}

/** The received-attachment fields a FORWARD reads from (a subset of both
 *  `MessageAttachment` and the Team Inbox `InboxMessage['attachment']`
 *  view-model). */
export interface ForwardableAttachment {
  kind: string;
  fileName: string | null;
  contentType: string | null;
  downloadURL: string | null;
  storagePath?: string | null;
  storageBucket?: string | null;
}

/**
 * PURE — build the `AttachmentInput` for FORWARDING an already-received
 * attachment to another conversation, WITHOUT re-uploading. Reuses the
 * received bytes: `storagePath` (+ `storageBucket`) point the server at the
 * existing object, and `link` carries the current `downloadURL` when we have
 * one (the server mints a fresh signed URL from `storagePath` when it is
 * empty/expired). `kind` is re-normalised to WhatsApp's only two media kinds
 * via `attachKindOf(contentType)`.
 *
 * Returns `null` when the attachment can NOT be forwarded — no `storagePath`
 * AND no usable `downloadURL` — so the caller shows a plain "can't forward"
 * message instead of firing a doomed send.
 */
export function buildForwardAttachment(
  a: ForwardableAttachment | null | undefined,
): AttachmentInput | null {
  if (!a) return null;
  const storagePath = a.storagePath || '';
  const link = a.downloadURL || '';
  // Need at least one way for the server to reach the bytes.
  if (!storagePath && !link) return null;
  const contentType = a.contentType || 'application/octet-stream';
  return {
    kind: attachKindOf(contentType),
    link,
    fileName: a.fileName || 'file',
    contentType,
    storagePath,
    storageBucket: a.storageBucket ?? null,
  };
}

/**
 * PURE — can this conversation accept an attachment, and if not, the
 * exact reason to show the user.
 *
 * ONE hard gate (Meta's, not ours): media is free-form, so WhatsApp
 * blocks it OUTSIDE the 24h window (a file can never ride a template).
 * Attach is disabled only when the window is closed, with the honest
 * reason. Whenever the window is OPEN and there is a real recipient (any
 * conversation the composer lets you send TEXT to — project chats,
 * known-contact chats, agent/bot chats, unknown-number triage rows), the
 * 📎 control is enabled. The bytes upload under `projects/{pid}/artefacts/`
 * for a project chat and under `projects/triage/artefacts/` otherwise
 * (both rules-permitted for any authenticated staff member); the server
 * records the openable attachment on the conversation.
 *
 * Returns { ok:true } or { ok:false, reason } — the composer uses this
 * to enable/disable the 📎 control and to message the user.
 */
export function attachAvailability(
  row: { projectId: string | null; triage?: boolean } | null,
  window: { open: boolean } | null,
): { ok: true } | { ok: false; reason: string } {
  if (!row) {
    return {
      ok: false,
      reason: 'Open a conversation before attaching a file.',
    };
  }
  if (!window || !window.open) {
    return {
      ok: false,
      reason:
        'The 24-hour window is closed. WhatsApp does not allow sending a ' +
        'file outside the window (a file cannot go in a template). Send a ' +
        'link in an approved template, or wait for the client to message.',
    };
  }
  return { ok: true };
}

/**
 * Upload the picked file to Firebase Storage and return the metadata the
 * send callable needs. REUSES the shipped projectFiles.uploadProjectFile
 * path/discipline EXACTLY (type+size pre-check, projects/{pid}/artefacts/
 * {fileId}/{name}, artefact metadata doc) — so the file ALSO appears in
 * the project's Files list and is governed by the same live rules. No new
 * Storage path, no rules change. Lazy-imports so the pure helpers above
 * stay SDK-free for the Node test runner.
 */
export async function uploadMessageAttachment(
  projectId: string,
  file: File,
  uploader: { uid: string; name: string },
): Promise<AttachmentInput> {
  const { uploadProjectFile } = await import('./projectFiles');
  const { getDownloadURL, ref } = await import('firebase/storage');
  const { storage } = await import('./firebase');

  // Reuse the proven upload (validates type+size, writes the artefact
  // doc, lands under projects/{pid}/artefacts/{fileId}/{safeName}).
  const fileId = await uploadProjectFile(projectId, file, uploader);

  // Re-derive the same safe name + path projectFiles minted, then read
  // back the download URL Meta will fetch the bytes from.
  const { safeFileName } = await import('./projectFiles');
  const safe = safeFileName(file.name);
  const storagePath = `projects/${projectId}/artefacts/${fileId}/${safe}`;
  const link = await getDownloadURL(ref(storage, storagePath));

  return {
    kind: attachKindOf(file.type),
    link,
    fileName: safe,
    contentType: file.type || 'application/octet-stream',
    storagePath,
  };
}

/* ════════════════════ callable transport ════════════════════ */

/**
 * A server-side LENS filter for the conversation list. Mutually exclusive:
 * supplying BOTH `contactId` and `projectId` is rejected server-side
 * (`invalid-argument`). Filtered mode returns up to 200 rows and a null
 * cursor. The Person/Project lenses pass this so they read the SAME live
 * `wa_conversations` source as the Team Inbox — one source, many views —
 * instead of a parallel copy.
 */
export type ConversationFilter =
  | { contactId: string }
  | { projectId: string };

/**
 * Fetch the visible conversation list, optionally with ONE conversation's
 * full thread + its live 24h window. Lazy-imports firebase so the pure
 * helpers above stay unit-testable without the SDK.
 *
 * `filter` (additive, 3rd arg) narrows the list SERVER-SIDE to one contact
 * or one project — the only correct way to scope a lens, because the list is
 * paged newest-first and a client-side filter would miss any thread whose
 * last message falls outside the first page. Existing callers (0-2 args) are
 * untouched.
 */
export async function fetchConversations(
  conversationKey?: string,
  page?: { cursor?: number; limit?: number },
  filter?: ConversationFilter,
): Promise<ListResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    {
      conversationKey?: string;
      cursor?: number;
      limit?: number;
      contactId?: string;
      projectId?: string;
    },
    ListResult
  >(functions, 'waListConversations');
  const payload: {
    conversationKey?: string;
    cursor?: number;
    limit?: number;
    contactId?: string;
    projectId?: string;
  } = {};
  if (conversationKey) payload.conversationKey = conversationKey;
  if (page && typeof page.cursor === 'number') payload.cursor = page.cursor;
  if (page && typeof page.limit === 'number') payload.limit = page.limit;
  if (filter && 'contactId' in filter && filter.contactId) {
    payload.contactId = filter.contactId;
  } else if (filter && 'projectId' in filter && filter.projectId) {
    payload.projectId = filter.projectId;
  }
  const res = await fn(payload);
  return res.data;
}

/**
 * Send EXACTLY ONE manual reply into ONE conversation. The server
 * re-checks visibility + the 24h window + the approved-template allow-list
 * and records the sent reply into the project thread + communications. The
 * call shape carries ONE message — there is deliberately no array /
 * recipient-list / broadcast parameter (incident discipline:
 * feedback_no_unapproved_bulk_client_sends).
 */
export async function sendManualReply(input: {
  conversationKey: string;
  text?: string;
  templateName?: string;
  templateParams?: string[];
  /** ONE attachment → media path (server-gated to the open 24h window).
   *  Still ONE explicit message: there is no array/recipient shape. */
  attachment?: AttachmentInput;
}): Promise<SendResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<typeof input, SendResult>(
    functions,
    'waSendManualReply',
  );
  const res = await fn(input);
  return res.data;
}

/** Result of the AD-028 react callable. */
export interface ReactResult {
  ok: boolean;
  /** The wamid of the reaction message Meta returned (null on a removal/no-id). */
  waMessageId: string | null;
  /** true when the call REMOVED a reaction (empty emoji). */
  removed: boolean;
  /** true when the badge was persisted on the target message doc. */
  pinned?: boolean;
}

/**
 * React to (or un-react from) ONE existing message with an emoji (AD-028).
 *
 * Sends a Cloud-API reaction to `targetWaMessageId` and pins the badge on the
 * target message doc's `reactions` map under key `'self'`. An EMPTY `emoji`
 * REMOVES the reaction (un-react). Same ONE-message discipline as
 * `sendManualReply` — no bulk shape exists.
 */
export async function reactToMessage(input: {
  conversationKey: string;
  targetWaMessageId: string;
  /** '' removes the reaction (un-react). */
  emoji: string;
}): Promise<ReactResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<typeof input, ReactResult>(
    functions,
    'waReactToMessage',
  );
  const res = await fn(input);
  return res.data;
}

/**
 * Clear ONE conversation's unread badge — called when the team OPENS it.
 *
 * The unread counter lives on the SERVER-ONLY `wa_conversations` index
 * (firestore.rules default-deny → the portal can neither read nor write it
 * directly). So "reading clears unread" must go through this callable, which
 * re-checks the caller's visibility and writes `unreadByTeam:0` via the admin
 * SDK. Idempotent server-side (a row already at 0 is a no-op), so it is safe
 * to call on every open and on every poll-driven re-open. Best-effort: a
 * failure must NOT block opening the conversation — the caller swallows it.
 */
export async function markConversationRead(
  conversationKey: string,
): Promise<MarkReadResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<{ conversationKey: string }, MarkReadResult>(
    functions,
    'waMarkConversationRead',
  );
  const res = await fn({ conversationKey });
  return res.data;
}

export interface AiPausedResult {
  ok: boolean;
  aiPaused: boolean;
  /** true → the server actually flipped the flag (vs. it was already so). */
  changed: boolean;
}

/**
 * "Take over / Resume AI" on ONE conversation (item 5). The aiPaused flag
 * lives on the SERVER-ONLY `wa_conversations` index (default-deny rules), so
 * — exactly like markConversationRead — the portal cannot write it directly
 * and must go through this admin-SDK callable. The server re-checks the
 * caller's visibility (same gate as the list/thread-open path) before
 * writing. When paused, the inbound AI-auto-reply path skips its auto-reply
 * and the human handles the chat manually (existing dual-number send path).
 */
export async function setConversationAiPaused(
  conversationKey: string,
  paused: boolean,
): Promise<AiPausedResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    { conversationKey: string; paused: boolean },
    AiPausedResult
  >(functions, 'waSetConversationAiPaused');
  const res = await fn({ conversationKey, paused });
  return res.data;
}

export interface ResolveTriageResult {
  ok: boolean;
  /** true → the server actually cleared the flag (vs. it was already clear). */
  changed: boolean;
}

/**
 * Resolve (clear the triage flag on) ONE WhatsApp conversation row (Part 3 —
 * WA triage parity). After staff actions an unknown-number WA item (Create
 * Lead / Create Contact / Add-to-existing / Archive / Hold), the canonical
 * triage RECORD (`draft_contacts/{phone}`) is resolved client-side, but the
 * conversation LIST reads the SERVER-ONLY `wa_conversations` index whose
 * `triage:true` flag keeps the row in the Triage filter. Exactly like
 * setConversationAiPaused, the portal cannot write the index directly and
 * must go through this admin-SDK callable. The server re-checks visibility
 * (same gate as the list/thread-open path) before clearing.
 */
export async function resolveTriage(
  conversationKey: string,
  action: string,
): Promise<ResolveTriageResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    { conversationKey: string; action: string },
    ResolveTriageResult
  >(functions, 'waResolveTriage');
  const res = await fn({ conversationKey, action });
  return res.data;
}

/** Shape returned by `waGetThreadPage` — a page of older messages. */
export interface ThreadPageResult {
  key: string;
  messages: WorkspaceMessage[];
  hasMore: boolean;
  nextCursor: number | null;
}

/**
 * Fetch a page of OLDER messages for one conversation.
 * Call with the `nextCursor` from the previous thread load as `beforeMs`
 * to scroll back through history. Used by the thread scroll panel (D-3/D-4).
 */
export async function fetchThreadPage(
  conversationKey: string,
  beforeMs: number,
  limit = 50,
): Promise<ThreadPageResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    { conversationKey: string; beforeMs: number; limit: number },
    ThreadPageResult
  >(functions, 'waGetThreadPage');
  const res = await fn({ conversationKey, beforeMs, limit });
  return res.data;
}
